<!-- Always clickable -->
<div (click)="showNotifications($event)" style="cursor: pointer;"
	 class="notifications ms-1 me-3 ps-3">
	<!-- Lit whenever there is anything to find in here, snoozed included - the card flies into this
	     flag when it's snoozed, so a dark, empty-looking flag straight afterwards is a lie. -->
	<div #flagRef class="notifications-flag"
		 [ngClass]="(activeNotifications.length > 0 || snoozedNotifications.length > 0) ? 'flag-light' : 'flag-dark'"
		 [class.notifications-flag-pulse]="flagPulse">
		<i *ngIf="activeNotifications.length > 0" class="badge">
			{{activeNotifications.length}}
		</i>

		<!-- Nothing wanting attention but something parked: a quiet dot rather than a count, so a
		     snooze shows it's still here without nagging about it. -->
		<i *ngIf="activeNotifications.length === 0 && snoozedNotifications.length > 0"
		   class="badge badge-snoozed"
		   title="Snoozed notifications"></i>

		<i class="fas fa-flag fa-xl"></i>
	</div>
</div>


<!-- The alert stack: a notification lands here, under the flag, and stays until it is dismissed,
     snoozed or acted on.  Same card as the panel below - see the shared template.  Hidden while the
     panel is open, since that already lists everything.

     Moved to <body> on init, with the panel, for the stacking reason described in the component. -->
<div #notificationAlertsRef class="notification-alerts"
	 [ngClass]="{ 'notification-alerts-hidden': showingNotifications === true }">

	<!-- Bulk actions, once more than one has stacked up.  A single card already carries its own
	     Dismiss and Snooze, so this would just be a second way to do the same thing. -->
	<div *ngIf="alertNotifications.length > 1" class="notification-alerts-header">
		<div class="notification-alerts-count">{{alertNotifications.length}} notifications</div>

		<a href="javascript:void(0)" (click)="toggleAlertSnoozeOptions()">
			<i class="fal fa-clock me-1"></i>Snooze All
		</a>

		<a href="javascript:void(0)" (click)="dismissAllAlerts()">
			<i class="fal fa-xmark me-1"></i>Dismiss All
		</a>
	</div>

	<!-- Same durations a single card offers -->
	<div *ngIf="showAlertSnoozeOptions && alertNotifications.length > 1" class="notification-alerts-snooze-options">
		<button *ngFor="let snoozeOption of snoozeOptions"
				class="btn btn-xs btn-outline-primary"
				(click)="snoozeAllAlerts(snoozeOption.snoozeMinutes)">{{snoozeOption.text}}</button>
	</div>

	<!-- The key that flyToFlag matches on is on the card itself, inside the shared template, so a snooze
	     from the panel animates too - see cardElementsByKey(). -->
	<div *ngFor="let notification of alertNotifications; trackBy: trackByKey"
		 class="notification-alert">
		<ng-container *ngTemplateOutlet="notificationCard; context: { $implicit: notification }"></ng-container>
	</div>
</div>


<!-- The notification panel.  The flag opens it, and anything arriving opens it by itself - there is
     only one list, so a notification is never in two places wearing two different layouts.

     Moved to <body> on init - see the note in the component.  It has to sit in the page's own stacking
     context or page content paints straight over it. -->
<div #notificationPanelRef class="notification-details card card-body bg-body-tertiary text-body"
	 [ngClass]="{ 'fade-in': showingNotifications === true, 'fade-out': showingNotifications === false }">

	<!-- Header -->
	<div class="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
		<div style="font-size: 18px; font-weight: 700;">Notifications</div>
		<div class="d-flex align-items-center" style="gap: 14px;">
			<a href="javascript:void(0)"
			   *ngIf="activeNotifications.length > 0 || snoozedNotifications.length > 0"
			   (click)="clearAllNotifications()"
			   class="notification-icon text-decoration-none small">
				<i class="fa-solid fa-trash me-1" style="font-size: 12px; position: relative; top: -1px;"></i>
				Dismiss All
			</a>
			<a href="javascript:void(0)" (click)="hideNotificationWindow()"
			   class="notification-icon text-decoration-none" aria-label="Close">
				<i class="far fa-xmark"></i>
			</a>
		</div>
	</div>

	<!-- Showing now -->
	<div *ngFor="let notification of activeNotifications; trackBy: trackByKey">
		<ng-container *ngTemplateOutlet="notificationCard; context: { $implicit: notification }"></ng-container>
	</div>

	<!-- Snoozed.  Kept on the list rather than hidden, so a snooze can't quietly lose something - they
	     put themselves back at the top when the snooze runs out. -->
	<div *ngIf="snoozedNotifications.length > 0" class="notification-snoozed-header">
		<i class="fal fa-clock me-1"></i> Snoozed
	</div>

	<div *ngFor="let notification of snoozedNotifications; trackBy: trackByKey">
		<ng-container *ngTemplateOutlet="notificationCard; context: { $implicit: notification }"></ng-container>
	</div>

	<!-- If there are no notifications -->
	<div *ngIf="activeNotifications.length === 0 && snoozedNotifications.length === 0"
		 class="text-center text-muted p-2">
		No notifications
	</div>
</div>


<!-- One card, whatever the notification is and wherever it came from: what it is, what it says, then
     the same row of actions - dismiss, snooze, and a way to open what it's about. -->
<ng-template #notificationCard let-notification>
	<div class="notification-detail mb-2"
		 [attr.data-notification-key]="notification.key"
		 [ngClass]="'notification-detail-' + notification.tone"
		 [class.notification-detail-snoozed]="isSnoozed(notification)">

		<i class="fal {{notification.icon}} notification-detail-icon" aria-hidden="true"></i>

		<div class="notification-detail-content">
			<div class="notification-detail-header">
				<div class="notification-type-label">{{notification.typeLabel}}</div>
				<div class="notification-datetime">{{notification.notificationDate | date:'M/d/yy h:mm a'}}</div>
			</div>

			<!-- The message.  Several of them are written as a lead-in, then the subject, then the detail,
			     and they're styled by position - see the note on messageLines(). -->
			<div class="notification-message">
				<div *ngFor="let line of notification.lines; let i = index"
					 [ngClass]="{ 'notification-line-lead': i === 0 && notification.lines.length > 1, 'notification-line-subject': i === 1 }">
					{{line}}
				</div>
			</div>

			<div *ngIf="isSnoozed(notification)" class="notification-snoozed-until">
				<i class="fal fa-clock me-1"></i> Snoozed until {{notification.snoozeUntil | date:'M/d/yy h:mm a'}}
			</div>

			<!-- Actions -->
			<div class="notification-actions">
				<a href="javascript:void(0)" (click)="dismiss(notification)">
					<i class="fal fa-xmark me-1"></i>Dismiss
				</a>

				<a href="javascript:void(0)" (click)="toggleSnoozeOptions(notification)">
					<i class="fal fa-clock me-1"></i>Snooze
				</a>

				<a *ngIf="notification.jobId" href="javascript:void(0)" (click)="viewJob(notification)">
					<i class="fal fa-clipboard-list me-1"></i>View Job
				</a>

				<a *ngIf="notification.taskId" href="javascript:void(0)" (click)="viewTask(notification)">
					<i class="fal fa-list-check me-1"></i>View Task
				</a>

				<a *ngIf="notification.appointmentId" href="javascript:void(0)" (click)="viewAppointment(notification)">
					<i class="fal fa-calendar-day me-1"></i>View Appointment
				</a>

				<a *ngIf="notification.quickbooksTaskId" href="javascript:void(0)" (click)="viewQuickbooksTask(notification)">
					<i class="fal fa-eye me-1"></i>View QuickBooks Task
				</a>

				<a *ngIf="notification.route" href="javascript:void(0)" (click)="goToPage(notification)">
					<i class="fal fa-eye me-1"></i>{{notification.routeLabel}}
				</a>

				<a *ngIf="notification.notificationType === 'TaskReminder'" href="javascript:void(0)" (click)="completeTask($event, notification)">
					<span *ngIf="notification.isCompleting" class="waiting-gif"></span>
					<i *ngIf="!notification.isCompleting" class="fal fa-check-circle me-1"></i>Mark as Complete
				</a>
			</div>

			<!-- Snooze options, the same short list the task reminder has always offered -->
			<div *ngIf="notification.showSnoozeOptions" class="notification-snooze-options">
				<button *ngFor="let snoozeOption of snoozeOptions"
						class="btn btn-xs btn-outline-primary"
						(click)="snooze(notification, snoozeOption.snoozeMinutes)">{{snoozeOption.text}}</button>
			</div>
		</div>
	</div>
</ng-template>


<job-edit-dialog #jobEditDialogRef [jobModel]="jobModel" (onSave)="onJobSave()"></job-edit-dialog>
<quickbooks-task-display-dialog #quickbooksTaskDisplayDialog></quickbooks-task-display-dialog>
<task-edit-dialog #taskEditDialogRef></task-edit-dialog>
<appointment-dialog #appointmentDialogRef></appointment-dialog>
